import { z } from 'zod';
import { AccountIdentifierType } from '../enums/account-identifier-type.enum';

// ============================================================================
// CONSTANTS
// ============================================================================
const ACCOUNT_IDENTIFIER_CONSTANTS = {
  SORT_CODE_LENGTH: 8,
  ACCOUNT_NUMBER_MAX_LENGTH: 20,
  POLICY_NUMBER_MAX_LENGTH: 50,
} as const;

// ============================================================================
// LENIENT SCHEMAS (for deserialization)
// ============================================================================
const lenientBankAccountSchema = z.object({
  mode: z.literal(AccountIdentifierType.BankAccount),
  sortCode: z.string(),
  accountNumber: z.string(),
}).transform(val => ({
  mode: val.mode,
  sortCode: val.sortCode,
  accountNumber: val.accountNumber,
}));

const lenientPolicyNumberSchema = z.object({
  type: z.literal(AccountIdentifierType.PolicyNumber),
  policyNumber: z.string(),
}).transform(val => ({
  type: val.type,
  policyNumber: val.policyNumber,
}));

const lenientAccountIdentifierNodeSchema = z.union([
  lenientBankAccountSchema,
  lenientPolicyNumberSchema,
]);

// ============================================================================
// STRICT SCHEMAS (for validation)
// ============================================================================
const strictBankAccountSchema = z.object({
  mode: z.literal(AccountIdentifierType.BankAccount),
  sortCode: z.string()
    .length(ACCOUNT_IDENTIFIER_CONSTANTS.SORT_CODE_LENGTH, `Sort code must be exactly ${ACCOUNT_IDENTIFIER_CONSTANTS.SORT_CODE_LENGTH} characters`)
    .regex(/^\d{2}-\d{2}-\d{2}$/, 'Sort code must be in format ##-##-##'),

  accountNumber: z.string()
    .min(1, 'Account number is required')
    .max(ACCOUNT_IDENTIFIER_CONSTANTS.ACCOUNT_NUMBER_MAX_LENGTH, `Account number must be ${ACCOUNT_IDENTIFIER_CONSTANTS.ACCOUNT_NUMBER_MAX_LENGTH} characters or less`),
}).transform(val => ({
  mode: val.mode,
  sortCode: val.sortCode,
  accountNumber: val.accountNumber,
}));

const strictPolicyNumberSchema = z.object({
  type: z.literal(AccountIdentifierType.PolicyNumber),
  policyNumber: z.string()
    .min(1, 'Policy number is required')
    .max(ACCOUNT_IDENTIFIER_CONSTANTS.POLICY_NUMBER_MAX_LENGTH, `Policy number must be ${ACCOUNT_IDENTIFIER_CONSTANTS.POLICY_NUMBER_MAX_LENGTH} characters or less`),
}).transform(val => ({
  type: val.type,
  policyNumber: val.policyNumber,
}));

const strictAccountIdentifierNodeSchema = z.union([
  strictBankAccountSchema,
  strictPolicyNumberSchema,
]);

// ============================================================================
// TYPE
// ============================================================================
export type AccountIdentifierNode = z.infer<typeof lenientAccountIdentifierNodeSchema>;

// ============================================================================
// NAMESPACE (for Constants and Methods)
// ============================================================================
export namespace AccountIdentifierNode {
  export const Constants = ACCOUNT_IDENTIFIER_CONSTANTS;

  export function deserialize(data: unknown): AccountIdentifierNode {
    return lenientAccountIdentifierNodeSchema.parse(data);
  }

  export function create(data: unknown): AccountIdentifierNode {
    return strictAccountIdentifierNodeSchema.parse(data);
  }
}
